/******/ (() => { // webpackBootstrap
/* eslint-disable */
if (!customElements.get('variant-gallery-manager')) {
  customElements.define('variant-gallery-manager', class VariantGalleryManager extends HTMLElement {

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.galleries = {};

      // Match the section's "Image shape" setting so variant-gallery images are
      // cropped exactly like the default product-media-grid. Without this the
      // overlay renders images at their natural aspect ratio (height: auto), so a
      // single image with a different ratio appears larger than its siblings.
      // 'original' and 'adapt_to_first_image' keep the natural-ratio behavior.
      const shapeRatios = { square: '1 / 1', tall: '2 / 3', wide: '4 / 3' };
      this.imageAspectRatio = shapeRatios[this.dataset.imageShape] || null;
      // Inline crop style applied to every swapped-in image. Empty for shapes that
      // should keep natural proportions ('original' / 'adapt_to_first_image').
      this.cropStyle = this.imageAspectRatio
        ? `object-fit:cover;aspect-ratio:${this.imageAspectRatio};`
        : '';

      // Parse variant gallery JSON data embedded by Liquid.
      // The JSON is keyed by variant ID and maps to an array of image objects.
      // The Liquid template resolves color-sharing: all size variants for the same
      // color point to the same gallery, so a lookup by any variant.id gives the
      // right set of images for that color.
      const dataEl = document.getElementById('variant-galleries-' + this.sectionId);
      if (dataEl) {
        try {
          this.galleries = JSON.parse(dataEl.textContent);
        } catch (e) {
          console.warn('[variant-gallery-manager] Could not parse gallery data:', e);
        }
      }

      // Per-variant videos, keyed by variant ID. Appended as the final gallery tile,
      // but only for variants that also have an image gallery (see handleVariantChange).
      this.videos = {};
      const videoDataEl = document.getElementById('variant-videos-' + this.sectionId);
      if (videoDataEl) {
        try {
          this.videos = JSON.parse(videoDataEl.textContent);
        } catch (e) {
          console.warn('[variant-gallery-manager] Could not parse video data:', e);
        }
      }

      this.mediaWrapper = this.closest('[data-product-media-wrapper]');
      if (!this.mediaWrapper) return;

      this.mediaEls = Array.from(
        this.mediaWrapper.querySelectorAll('product-media, product-media-grid')
      );

      // Overlay div used for the desktop grid display path.
      // On mobile we swap slides directly into the existing Swiper instead.
      this.overlay = document.createElement('div');
      this.overlay.className = 'variant-gallery-overlay';
      this.mediaWrapper.appendChild(this.overlay);

      // Inject CSS once per page
      if (!document.getElementById('variant-gallery-styles')) {
        const style = document.createElement('style');
        style.id = 'variant-gallery-styles';
        style.textContent = `
          [data-product-media-wrapper].variant-gallery--active product-media,
          [data-product-media-wrapper].variant-gallery--active product-media-grid {
            display: none !important;
          }
          .variant-gallery-overlay {
            display: none;
          }
          [data-product-media-wrapper].variant-gallery--active .variant-gallery-overlay {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }
          [data-product-media-wrapper].variant-gallery--active .variant-gallery-overlay.variant-gallery--single {
            grid-template-columns: 1fr;
          }
          @media (max-width: 767px) {
            [data-product-media-wrapper].variant-gallery--active .variant-gallery-overlay {
              grid-template-columns: 1fr;
            }
          }
          .variant-gallery-overlay a {
            display: block;
            overflow: hidden;
          }
          .variant-gallery-overlay img {
            width: 100%;
            height: auto;
            display: block;
          }
          .variant-gallery-overlay .variant-gallery-video-tile {
            display: block;
            overflow: hidden;
          }
          .variant-gallery-video {
            width: 100%;
            height: auto;
            display: block;
          }
        `;
        document.head.appendChild(style);
      }

      this.onVariantChange = this.handleVariantChange.bind(this);

      // eventBus is a global defined by the theme. Attach as soon as it is available.
      // Other deferred scripts may load before or after this one, so we poll briefly.
      this.attachEventBus();

      // Apply the gallery for the server-rendered initially selected variant
      // (handles ?variant=ID URL param on page load).
      // On mobile, ProductMedia initialises its Swiper asynchronously via a deferred
      // script, so we poll until mainMediaSwiper is ready before applying.
      const mediaEl = this.mediaWrapper.querySelector('[data-selected-variant]');
      if (mediaEl) {
        try {
          const variant = JSON.parse(mediaEl.getAttribute('data-selected-variant'));
          if (variant) this.applyInitialVariant(variant);
        } catch (e) {}
      }

      // Watch for viewport crossing the mobile/desktop breakpoint (767px).
      // When the threshold is crossed while a variant gallery is active, we tear
      // down the current display mode and rebuild it for the new viewport so the
      // user always sees the Swiper on mobile and the grid on desktop.
      this._lastIsMobile = window.innerWidth <= 767;
      this._resizeTimer = null;
      this.onResize = () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => {
          const isMobile = window.innerWidth <= 767;
          if (isMobile !== this._lastIsMobile && this._currentImages) {
            this._lastIsMobile = isMobile;
            // Tear down whichever display mode was active, then rebuild for the
            // new viewport. Capture images + video before hideVariantGallery clears
            // them so we can re-apply immediately after.
            const images = this._currentImages;
            const video = this._currentVideo;
            this.hideVariantGallery();
            this.showVariantGallery(images, video);
          } else {
            this._lastIsMobile = isMobile;
          }
        }, 150);
      };
      window.addEventListener('resize', this.onResize);
    }

    attachEventBus() {
      if (typeof eventBus !== 'undefined') {
        eventBus.on('variant:change', this.onVariantChange);
        this.eventBusAttached = true;
      } else if (this.eventBusRetries < 20) {
        this.eventBusRetries = (this.eventBusRetries || 0) + 1;
        setTimeout(() => this.attachEventBus(), 100);
      }
    }

    // On mobile the Swiper is initialised by ProductMedia via a deferred script that
    // may run after this component connects. We poll until mainMediaSwiper exists
    // (up to 3 seconds) before applying the initial gallery, ensuring the Swiper
    // path is taken instead of falling back to the grid overlay.
    applyInitialVariant(variant, retries = 0) {
      if (window.innerWidth <= 767) {
        const productMediaEl = this.mediaWrapper.querySelector('product-media[data-device="mobile"]');
        const swiperReady = productMediaEl && productMediaEl.mainMediaSwiper;
        if (!swiperReady && retries < 30) {
          setTimeout(() => this.applyInitialVariant(variant, retries + 1), 100);
          return;
        }
      }
      this.handleVariantChange({ variant });
    }

    disconnectedCallback() {
      if (this.eventBusAttached && typeof eventBus !== 'undefined') {
        eventBus.off('variant:change', this.onVariantChange);
      }
      window.removeEventListener('resize', this.onResize);
    }

    handleVariantChange({ variant }) {
      if (!variant) return;
      const images = this.galleries[String(variant.id)];
      // Video only shows alongside an image gallery — a variant with a video but no
      // gallery falls through to hideVariantGallery and shows the default media.
      const video = this.videos[String(variant.id)] || null;
      if (images && images.length > 0) {
        this.showVariantGallery(images, video);
      } else {
        this.hideVariantGallery();
      }
    }

    // Builds the <video> markup for a variant video tile. autoplay+muted+loop+
    // playsinline gives the silent, self-playing showcase behavior. cropStyle keeps
    // it cropped to the same Image shape as the gallery images.
    videoElHtml(video) {
      if (!video || !video.sources || !video.sources.length) return '';
      const sources = video.sources
        .map((s) => `<source src="${this.escapeAttr(s.src)}" type="${this.escapeAttr(s.type)}">`)
        .join('');
      const poster = video.poster ? ` poster="${this.escapeAttr(video.poster)}"` : '';
      const dims = video.width && video.height ? ` width="${video.width}" height="${video.height}"` : '';
      return `<video class="theme-img variant-gallery-video" autoplay muted loop playsinline preload="metadata"${poster}${dims} style="${this.cropStyle}">${sources}</video>`;
    }

    // Display strategy:
    //   Mobile (≤767px) — swap the variant images directly into the existing Swiper
    //     carousel so the user gets the same peek/swipe/pagination experience as the
    //     default product media. The original slides are saved and restored on hide.
    //   Desktop (>767px) — hide product-media via a CSS class and show a grid overlay
    //     built from the variant images.
    // _currentImages tracks which images are active so the resize handler can
    // rebuild the correct display mode when the breakpoint is crossed.
    showVariantGallery(images, video) {
      this._currentImages = images;
      this._currentVideo = video || null;
      const videoHtml = this.videoElHtml(this._currentVideo);

      if (window.innerWidth <= 767) {
        const productMediaEl = this.mediaWrapper.querySelector('product-media[data-device="mobile"]');
        const swiper    = productMediaEl && productMediaEl.mainMediaSwiper;
        const wrapperEl = productMediaEl && productMediaEl.mainMedia && productMediaEl.mainMedia.querySelector('.swiper-wrapper');

        if (swiper && wrapperEl) {
          // Remove the desktop overlay class so product-media is never hidden
          // while we're using the Swiper path.
          this.mediaWrapper.classList.remove('variant-gallery--active');

          // Save the original slides once so they can be restored on hide or
          // when switching back to the default variant.
          if (!this._savedMobile) {
            this._savedMobile = {
              html:        wrapperEl.innerHTML,
              activeIndex: swiper.activeIndex
            };
          }

          const peepEnabled = productMediaEl.dataset.peepEnabled === 'true';
          const radiusClass = peepEnabled ? ' block-radius-clip' : '';
          const mobileVideoSlide = videoHtml
            ? `<div class="swiper-slide${radiusClass}" data-media-type="video">${videoHtml}</div>`
            : '';
          wrapperEl.innerHTML = images.map((img, i) => `
            <div class="swiper-slide${radiusClass}" data-media-type="image">
              <a
                data-main-media-link
                href="${this.escapeAttr(img.src)}"
                data-pswp-width="${img.width}"
                data-pswp-height="${img.height}"
                target="_blank"
              ><img
                class="theme-img"
                src="${this.escapeAttr(img.src)}"
                srcset="${this.escapeAttr(img.src400)} 400w, ${this.escapeAttr(img.src900)} 900w, ${this.escapeAttr(img.src)} 1445w"
                sizes="(min-width: 1200px) calc((1200px - 10rem) / 2), (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)"
                alt="${this.escapeAttr(img.alt)}"
                width="${img.width}"
                height="${img.height}"
                style="${this.cropStyle}"
                loading="${i === 0 ? 'eager' : 'lazy'}"
              /></a>
            </div>
          `).join('') + mobileVideoSlide;
          swiper.update();
          swiper.slideTo(0, 0, false);
          return;
        }
      }

      // Desktop path (or mobile fallback if Swiper is not yet ready).
      this.overlay.innerHTML = images.map((img, i) => `
        <a
          href="${this.escapeAttr(img.src)}"
          data-main-media-link
          data-pswp-width="${img.width}"
          data-pswp-height="${img.height}"
          target="_blank"
          class="block-radius-clip"
        >
          <img
            class="theme-img"
            src="${this.escapeAttr(img.src)}"
            srcset="${this.escapeAttr(img.src400)} 400w, ${this.escapeAttr(img.src900)} 900w, ${this.escapeAttr(img.src)} 1445w"
            sizes="(min-width: 768px) 50vw, 100vw"
            alt="${this.escapeAttr(img.alt)}"
            width="${img.width}"
            height="${img.height}"
            style="${this.cropStyle}"
            loading="${i === 0 ? 'eager' : 'lazy'}"
          />
        </a>
      `).join('') + (videoHtml ? `<div class="variant-gallery-video-tile block-radius-clip">${videoHtml}</div>` : '');

      // Single-column layout only when there is truly one tile (video counts too).
      const tileCount = images.length + (videoHtml ? 1 : 0);
      this.overlay.classList.toggle('variant-gallery--single', tileCount === 1);
      this.mediaWrapper.classList.add('variant-gallery--active');
    }

    hideVariantGallery() {
      // Restore mobile Swiper to its original slides if we swapped them.
      if (this._savedMobile) {
        const productMediaEl = this.mediaWrapper.querySelector('product-media[data-device="mobile"]');
        const swiper    = productMediaEl && productMediaEl.mainMediaSwiper;
        const wrapperEl = productMediaEl && productMediaEl.mainMedia && productMediaEl.mainMedia.querySelector('.swiper-wrapper');
        if (swiper && wrapperEl) {
          wrapperEl.innerHTML = this._savedMobile.html;
          swiper.update();
          swiper.slideTo(this._savedMobile.activeIndex, 0, false);
        }
        this._savedMobile = null;
      }

      // Stop any overlay video so it isn't playing while display:none.
      this.overlay.querySelectorAll('video').forEach((v) => {
        try { v.pause(); } catch (e) {}
      });

      this.mediaWrapper.classList.remove('variant-gallery--active');
      this._currentImages = null;
      this._currentVideo = null;
    }

    escapeAttr(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
  });
}
/******/ })();
